import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { HiArrowRight } from "react-icons/hi2";
import { ProductLineBadge, SectionCard } from "@/components/ui/detail/primitives";

/**
 * "Modele" section: the distinct agents (models) reachable from this client's scenarios.
 * There is no direct client→agent link in the domain — an agent is bound to a client only
 * through a scenario (Scenario.OwnerId + Scenario.AgentId), so this list is derived from the
 * already-loaded scenario list.
 */
const ClientModelsSection = ({ scenarios }) => {
    const navigate = useNavigate();

    const models = useMemo(() => {
        const byId = new Map();
        for (const s of scenarios) {
            if (!s.agent?.id) continue;
            const existing = byId.get(s.agent.id);
            if (existing) {
                existing.scenarioCount += 1;
            } else {
                byId.set(s.agent.id, {
                    id: s.agent.id,
                    name: s.agent.name,
                    description: s.agent.description,
                    type: s.agent.type ?? s.type ?? null,
                    scenarioCount: 1,
                });
            }
        }
        return [...byId.values()];
    }, [scenarios]);

    return (
        <SectionCard title="Modele">
            {models.length === 0 ? (
                <Text color="text.grey" fontSize="sm">
                    Brak modeli — żaden przypisany scenariusz nie ma podpiętego agenta.
                </Text>
            ) : (
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                    {models.map((m) => (
                        <HStack
                            key={m.id}
                            as="button"
                            type="button"
                            onClick={() => navigate(`/models/${m.id}`)}
                            justify="space-between"
                            align="flex-start"
                            textAlign="left"
                            bg="bg.secondary"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius={10}
                            p={4}
                            _hover={{ borderColor: "border.secondary", bg: "bg.highlight" }}
                            transition="background 0.15s, border-color 0.15s"
                        >
                            <VStack align="flex-start" gap={1.5} minW="0">
                                <HStack gap={2} flexWrap="wrap">
                                    <Text color="white" fontSize="sm" fontWeight={600} truncate>{m.name}</Text>
                                    <ProductLineBadge type={m.type} />
                                </HStack>
                                {m.description && (
                                    <Text color="text.grey" fontSize="xs" lineClamp={2}>{m.description}</Text>
                                )}
                                <Text color="text.grey" fontSize="xs">
                                    {m.scenarioCount} {m.scenarioCount === 1 ? "scenariusz" : "scenariusze"}
                                </Text>
                            </VStack>
                            <Box color="text.grey" flexShrink={0}><HiArrowRight /></Box>
                        </HStack>
                    ))}
                </Box>
            )}
        </SectionCard>
    );
};

export default ClientModelsSection;
